var YouboraObject = require('../object');
var Log = require('../log');
var ProductAnalyticsConfig = require('./productanalytics_config');
var ProductAnalyticsUserState = require('./productanalytics_userstate');
var YouboraProductAnalytics = YouboraObject.extend( /** @lends youbora.ProductAnalytics.prototype */
{
  /**
   * This class is the base of productanalytics. Every plugin will have an instance.
   *
   * @param {youbora.Plugin} plugin Parent plugin.
   *
   * @constructs youbora.ProductAnalytics
   * @extends youbora.Emitter
   * @memberof youbora
   */
  constructor: function (plugin) {
    /** Parent {@link youbora.Plugin} reference. */
    this._plugin = typeof plugin === 'undefined' ? null : plugin;
    this._infinity = this._plugin && this._plugin.infinity ? this._plugin.infinity : null;
    this._initialized = false;

    // Product analytics attributes
    this._productAnalyticsSettingsDefault = {
      autoTrackNavigation: true,
      autoTrackAttribution: true,
      highlightContentAfter: 1000,
      enableStateTracking: false,
      activeStateTimeout: 30000,
      activeStateDimension: 9,
      pageRules: []
    };
    this._productAnalyticsSettings = Object.assign({}, this._productAnalyticsSettingsDefault);
    this._page = null;
    this._userState = null;
    this._config = null;
    this._searchQuery = null;
    this._location = typeof window === 'undefined' || typeof window.location === 'undefined' ? null : window.location;
    this._contentHighlighted = null;
    this._contentHighlightTimeout = null;
    if (!this._plugin) {
      Log.warn('Plugin reference unset.');
    }
    if (!this._infinity) {
      Log.warn('Infinity reference unset.');
    }
    if (!this._location) {
      Log.warn('Location reference unset.');
    }
  },
  /**
    * Initializes product analytics
    * @param {Object} productAnalyticsSettings product analytics settings
    * @param {Object} [npawtmConfigValue] configuration settings
    */
  initialize: function (productAnalyticsSettings) {
    var query;

    // Load settings

    this._productAnalyticsSettings = Object.assign({}, this._productAnalyticsSettingsDefault);
    if (typeof productAnalyticsSettings === 'object') {
      Object.assign(this._productAnalyticsSettings, productAnalyticsSettings);
    }

    // Validate settings

    if (typeof this._productAnalyticsSettings.autoTrackNavigation !== 'boolean') {
      this._productAnalyticsSettings.autoTrackNavigation = this._productAnalyticsSettingsDefault.autoTrackNavigation;
    }
    if (typeof this._productAnalyticsSettings.autoTrackAttribution !== 'boolean') {
      this._productAnalyticsSettings.autoTrackAttribution = this._productAnalyticsSettingsDefault.autoTrackAttribution;
    }
    if (typeof this._productAnalyticsSettings.highlightContentAfter !== 'number') {
      this._productAnalyticsSettings.highlightContentAfter = this._productAnalyticsSettingsDefault.highlightContentAfter;
    } else if (this._productAnalyticsSettings.highlightContentAfter < 1000) {
      Log.warn('Invalid higlightContentAfter value. Using default value instead.');
      this._productAnalyticsSettings.highlightContentAfter = this._productAnalyticsSettingsDefault.highlightContentAfter;
    }
    if (typeof this._productAnalyticsSettings.enableStateTracking !== 'boolean') {
      this._productAnalyticsSettings.enableStateTracking = this._productAnalyticsSettingsDefault.enableStateTracking;
    }
    if (typeof this._productAnalyticsSettings.activeStateTimeout !== 'number') {
      this._productAnalyticsSettings.activeStateTimeout = this._productAnalyticsSettingsDefault.activeStateTimeout;
    }
    if (typeof this._productAnalyticsSettings.activeStateDimension !== 'number' || this._productAnalyticsSettings.activeStateDimension < 1 || this._productAnalyticsSettings.activeStateDimension > 9) {
      this._productAnalyticsSettings.activeStateDimension = this._productAnalyticsSettingsDefault.activeStateDimension;
    }
    if (typeof this._productAnalyticsSettings.pageRules === 'undefined' || !Array.isArray(this._productAnalyticsSettings.pageRules)) {
      this._productAnalyticsSettings.pageRules = this._productAnalyticsSettingsDefault.pageRules;
    }

    // Identify current page

    this._config = new ProductAnalyticsConfig(this._fireEvent.bind(this));
    this._identify();

    // Set as initialized

    this._initialized = true;

    // Track navigation

    if (this._productAnalyticsSettings.autoTrackNavigation) {
      this._trackNavigation();
    }

    // Track attribution

    if (this._productAnalyticsSettings.autoTrackAttribution) {
      query = this._location === null ? '' : this._location.search;
      this._trackAttribution(new URLSearchParams(query));
    }
  },
  /**
   * Identify current page
   * @private
   */

  _identify: function () {
    var regex;
    var r;
    var i;

    // Try to identify using config approach

    if (this._config === null) {
      this._page = null;
    } else {
      this._page = this._config.identify(this._location);
    }

    // Try to identify using pageRules setting

    for (i = 0; i < this._productAnalyticsSettings.pageRules.length && this._page === null && this._location !== null; i++) {
      r = this._productAnalyticsSettings.pageRules[i];
      if (typeof r.rule !== 'undefined' && typeof r.page !== 'undefined') {
        try {
          regex = new RegExp(r.rule);
        } catch (ex) {
          regex = null;
          Log.warn('Error processing rule', r.rule, r.page);
        }
        if (regex !== null && this._location.pathname.match(regex) !== null) {
          this._page = this._location.pathname.replace(regex, r.page);
        }
      }
    }

    // If identication has failed, set pathname as page name

    if (this._page === null && this._location !== null) {
      this._page = this._location.pathname;
    }
  },
  // -------------------------------------------------------------------------------------------
  // ADAPTER
  // -------------------------------------------------------------------------------------------

  /**
    * Track adapter start
    * @private
    */

  _adapterTrackStart: function () {
    if (this._initialized) {
      this.trackPlayerInteraction('start', {}, {}, true);
    }
  },
  /**
    * Execute after adapter is set to plugin
    */

  adapterAfterSet: function () {
    var adapter;
    if (this._initialized) {
      // Set active state

      if (this._productAnalyticsSettings.enableStateTracking) {
        if (this._userState !== null) {
          Log.warn('userState is already initialized.');
        }
        if (!this._plugin) {
          Log.warn('Cannot track user state since plugin is unavailable.');
        } else if (!this._plugin.setOptions) {
          Log.warn('Cannot track user state since plugin options are unavailable.');
        } else {
          this._userState = new ProductAnalyticsUserState(this._productAnalyticsSettings.activeStateDimension, this._productAnalyticsSettings.activeStateTimeout, this._fireAdapterEvent.bind(this), this._plugin.setOptions.bind(this._plugin));
        }
      } else {
        this._userState = null;
      }

      // Track player interaction

      adapter = this._adapterGet();
      if (adapter) {
        adapter.on('start', this._adapterTrackStart.bind(this));
      } else {
        Log.warn('Cannot bind adapter start since it is unavailable.');
      }
    }
  },
  /**
    * Execute before removing adapter from plugin
    */

  adapterBeforeRemove: function () {
    var adapter;
    adapter = this._adapterGet();
    if (adapter) {
      adapter.off('start', this._adapterTrackStart);
    } else if (this._initialized) {
      Log.warn('Cannot unbind adapter start since it is unavailable.');
    }
    this._userState = null;
  },
  /**
    * Get adapter
    * @private
    */

  _adapterGet: function () {
    var adapter;
    if (!this._plugin) {
      adapter = null;
    } else if (typeof this._plugin.getAdapter !== 'function') {
      adapter = null;
    } else {
      adapter = this._plugin.getAdapter();
    }
    return typeof adapter === 'undefined' ? null : adapter;
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // SESSION
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * New user session
    */

  newSession: function () {
    var executed = false;
    if (!this._initialized) {
      Log.warn('Cannot start a new session since Product Analytics is uninitialized.');
    } else if (!this._infinity) {
      Log.warn('Cannot start a new session since infinity is unavailable.');
    } else {
      this._infinity.newSession();
      executed = true;
    }
    return executed;
  },
  /**
    * Ends user session
    */

  endSession: function () {
    var executed = false;
    if (!this._initialized) {
      Log.warn('Cannot end session since Product Analytics is uninitialized.');
    } else if (!this._infinity) {
      Log.warn('Cannot end session since infinity is unavailable.');
    } else {
      this._infinity.fireSessionStop();
      executed = true;
    }
    return executed;
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // NAVIGATION
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Tracks navigation
    * @param {string} pageName The unique name to identify a page of the application.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackNavigation: function (pageName, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track navigation since Product Analytics is uninitialized.');
    } else if (typeof pageName !== 'string' || pageName === '') {
      Log.warn('Cannot track navigation since page has not been supplied.');
    } else {
      this._page = pageName;
      this._trackNavigation(dimensions, metrics);
    }
  },
  /**
    * Tracks navigation (either manually or automatically)
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    * @private
    */
  _trackNavigation: function (dimensions, metrics) {
    var route;
    var path;
    var host;

    // Get UTM Options

    if (this._location === null) {
      route = '';
      path = '';
      host = '';
    } else {
      route = this._location.href;
      path = this._location.pathname;
      host = this._location.hostname;
    }
    if (!this._initialized) {
      Log.warn('Cannot track navigation since Product Analytics is uninitialized.');
    } else if (!this._infinity) {
      Log.warn('Cannot track navigation since infinity is unavailable.');
    } else {
      this._infinity.begin({
        route: route,
        page: this._page
      });
      Log.notice('[NAV]', this._page);
      this._fireEvent('[NAV] ' + this._page, {
        eventType: 'Navigation',
        route: path,
        routeDomain: host,
        fullRoute: route
      }, dimensions, metrics);
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // ATTRIBUTION
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Tracks attribution
    * @param {string} utmSource The UTM Source parameter. It is commonly used to identify a search engine, newsletter, or other source (i.e., Google, Facebook, etc.).
    * @param {string} utmMedium The UTM Medium parameter. It is commonly used to identify a medium such as email or cost-per-click (cpc).
    * @param {string} utmCampaign The UTM Campaign parameter. It is commonly used for campaign analysis to identify a specific product promotion or strategic campaign (i.e., spring sale).
    * @param {string} utmTerm The UTM Term parameter. It is commonly used with paid search to supply the keywords for ads (i.e., Customer, NonBuyer, etc.).
    * @param {string} utmContent The UTM Content parameter. It is commonly used for A/B testing and content-targeted ads to differentiate ads or links that point to the same URL (i.e., Banner1, Banner2, etc.)
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackAttribution: function (utmSource, utmMedium, utmCampaign, utmTerm, utmContent, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track attribution since Product Analytics is uninitialized.');
    } else if (this._productAnalyticsSettings.autoTrackAttribution) {
      Log.warn('Automatic attribution tracking is enabled: this request won\'t be processed.');
    } else {
      var params;
      params = {};
      if (typeof utmSource !== 'undefined') params.utm_source = utmSource;
      if (typeof utmMedium !== 'undefined') params.utm_medium = utmMedium;
      if (typeof utmCampaign !== 'undefined') params.utm_campaign = utmCampaign;
      if (typeof utmTerm !== 'undefined') params.utm_term = utmTerm;
      if (typeof utmContent !== 'undefined') params.utm_content = utmContent;
      this._trackAttribution(new URLSearchParams(params), dimensions, metrics);
    }
  },
  /**
    * Tracks attribution (either manually or automatically)
    * @param {URLSearchParams} params Object where to look for UTM params.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    * @private
    */
  _trackAttribution: function (params, dimensions, metrics) {
    // Get UTM parameters

    var utmParams = this._getUTMParams(params);
    var url = this._location === null ? '' : this._location.href;

    // Track attribution

    if (!this._initialized) {
      Log.warn('Cannot track attribution since Product Analytics is uninitialized.');
    } else if (utmParams) {
      Log.notice('ATTRIBUTION');
      this._fireEvent('ATTRIBUTION', Object.assign({
        eventType: 'Attribution',
        url: url
      }, utmParams), dimensions, metrics);
    }
  },
  /**
   * Retrieves UTM params from querystring (utm_source, utm_medium, utm_campaign, utm_term, utm_content) and returns an object containing them but
   * with key formatted as specified by utmFormatDot.
   * @param {URLSearchParams} params Object where to look for UTM params
   * @returns {{}}
   * @private
   */
  _getUTMParams: function (params) {
    var utmParams;
    var keySplitted;
    var keyIndex;
    var keys;
    var key;
    keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmParams = {};

    // Look for UTM params and add them to the newly constructed object converting key as specified by utmFormatDot

    params.forEach(function (paramValue, paramKey) {
      keyIndex = keys.indexOf(paramKey.toLowerCase());
      if (keyIndex !== -1) {
        key = keys[keyIndex];
        keySplitted = key.split('_');
        if (keySplitted.length > 1) {
          key = keySplitted[0] + keySplitted[1].charAt(0).toUpperCase() + keySplitted[1].slice(1);
        }
        utmParams[key] = paramValue;
      }
    });
    return Object.keys(utmParams).length > 0 ? utmParams : null;
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // SECTION
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Section goes into viewport.
    * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
    * @param {Number} sectionOrder The section order within the page.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackSectionIn: function (section, sectionOrder, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track section-in since Product Analytics is uninitialized.');
    } else if (typeof section !== 'string' || section === '') {
      Log.warn('Cannot track section-in since no section has been supplied.');
    } else if (typeof sectionOrder !== 'number' || sectionOrder < 1) {
      Log.warn('Cannot track section-in since sectionOrder is invalid.');
    } else {
      Log.notice('[SECTION] In');
      this._fireEvent('SECTION IN', {
        eventType: 'SectionVisibility',
        sectionOrder: sectionOrder,
        section: section
      }, dimensions, metrics);
    }
  },
  /**
    * Section goes out of viewport.
    * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
    * @param {Number} sectionOrder The section order within the page.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackSectionOut: function (section, sectionOrder, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track section-out since Product Analytics is uninitialized.');
    } else if (typeof section !== 'string' || section === '') {
      Log.warn('Cannot track section-out since no section has been supplied.');
    } else if (typeof sectionOrder !== 'number' || sectionOrder < 1) {
      Log.warn('Cannot track section-out since sectionOrder is invalid.');
    } else {
      Log.notice('[SECTION] Out');
      this._fireEvent('SECTION OUT', {
        eventType: 'SectionVisibility',
        sectionOrder: sectionOrder,
        section: section
      }, dimensions, metrics);
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // CONTENT
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Sends a content highlight event if content is focused during, at least, highlightContentAfter ms.
    * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
    * @param {Number} sectionOrder The section order within the page.
    * @param {integer} column Used to indicate the column number where content is placed in a grid layout The first column is number 1.
    * @param {integer} row Used to indicate the row number where content is placed in a grid layout. The first row is number 1. In the case of a horizontal list instead of a grid, the row parameter should be set to 1.
    * @param {string} contentID The unique content identifier of the content linked.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  contentFocusIn: function (section, sectionOrder, column, row, contentID, dimensions, metrics) {
    this.contentFocusOut();
    if (!this._initialized) {
      Log.warn('Cannot track content highlight since Product Analytics is uninitialized.');
    } else if (typeof section !== 'string' || section === '') {
      Log.warn('Cannot track content highlight since no section has been supplied.');
    } else if (typeof sectionOrder !== 'number' || sectionOrder < 1) {
      Log.warn('Cannot track content highlight since sectionOrder is invalid.');
    } else if (typeof column !== 'number' || column < 1) {
      Log.warn('Cannot track content highlight since column is invalid.');
    } else if (typeof row !== 'number' || row < 1) {
      Log.warn('Cannot track content highlight since row is invalid.');
    } else if (typeof contentID !== 'string' || contentID === '') {
      Log.warn('Cannot track content highlight since no contentID has been supplied.');
    } else {
      this._contentHighlighted = {
        sectionOrder: sectionOrder,
        section: section,
        column: column,
        row: row,
        contentID: contentID,
        dimensions: dimensions,
        metrics: metrics
      };
      this._contentHighlightTimeout = setTimeout(this._trackContentHiglight.bind(this), this._productAnalyticsSettings.highlightContentAfter);
    }
  },
  /**
    * Content loses focus
    * @private
    */

  contentFocusOut: function () {
    if (this._contentHighlightTimeout !== null) {
      clearTimeout(this._contentHighlightTimeout);
    }
    this._contentHighlighted = null;
    this._contentHighlightTimeout = null;
  },
  /**
    * Sends a content highlight event using selected content info
    * @private
    */

  _trackContentHiglight: function () {
    if (this._contentHighlighted) {
      Log.notice('CONTENT HIGHLIGHT');
      this._fireEvent('CONTENT HIGHLIGHT', {
        eventType: 'ContentHighlight',
        sectionOrder: this._contentHighlighted.sectionOrder,
        section: this._contentHighlighted.section,
        column: this._contentHighlighted.column,
        row: this._contentHighlighted.row,
        contentId: this._contentHighlighted.contentID
      }, this._contentHighlighted.dimensions, this._contentHighlighted.metrics);
    } else {
      Log.warn('Cannot highlight content since no content is selected');
    }
  },
  /**
    * Tracks the location of user clicks.
    * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
    * @param {Number} sectionOrder The section order within the page.
    * @param {integer} column Used to indicate the column number where content is placed in a grid layout The first column is number 1.
    * @param {integer} row Used to indicate the row number where content is placed in a grid layout. The first row is number 1. In the case of a horizontal list instead of a grid, the row parameter should be set to 1.
    * @param {string} contentID The unique content identifier of the content linked.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackContentClick: function (section, sectionOrder, column, row, contentID, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track content click since Product Analytics is uninitialized.');
    } else if (typeof section !== 'string' || section === '') {
      Log.warn('Cannot track content click since no section has been supplied.');
    } else if (typeof sectionOrder !== 'number' || sectionOrder < 1) {
      Log.warn('Cannot track content click since sectionOrder is invalid.');
    } else if (typeof column !== 'number' || column < 1) {
      Log.warn('Cannot track content click since column is invalid.');
    } else if (typeof row !== 'number' || row < 1) {
      Log.warn('Cannot track content click since row is invalid.');
    } else if (typeof contentID !== 'string' || contentID === '') {
      Log.warn('Cannot track content click since no contentID has been supplied.');
    } else {
      Log.notice('CONTENT CLICK');
      this._fireEvent('CONTENT CLICK', {
        eventType: 'ContentClick',
        sectionOrder: sectionOrder,
        section: section,
        column: column,
        row: row,
        contentId: contentID
      }, dimensions, metrics);
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // CONTENT PLAYBACK
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Tracks when a content starts playing be it automatically or through a user interaction.
    * @param {string} contentID The unique content identifier of the content being played.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackPlay: function (contentID, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track play since Product Analytics is uninitialized.');
    } else if (typeof contentID !== 'string' || contentID === '') {
      Log.warn('Cannot track play since no contentID has been supplied.');
    } else {
      Log.notice('[PLAYER] Play');
      this._fireAdapterEvent('[PLAYER] Play', {
        eventType: 'ContentPlayback',
        contentId: contentID
      }, dimensions, metrics);
      if (this._userState !== null) {
        this._userState.setActive('Play');
      }
    }
  },
  /**
    * Tracks content watching events.
    * TODO: add (2nd) argument to tell whether user state must be updated or not
    * @param {string} eventName The name of the interaction (i.e., Pause, Seek, Skip Intro, Skip Ads, Switch Language, etc.).
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    * @param {boolean} [playerStarted] Internal param informing that current interaction is responsible of first player start
    */

  trackPlayerInteraction: function (eventName, dimensions, metrics, playerStarted) {
    if (!this._initialized) {
      Log.warn('Cannot track player interaction since Product Analytics is uninitialized.');
    } else if (typeof eventName !== 'string' || eventName === '') {
      Log.warn('Cannot track player interaction since no interaction name has been supplied.');
    } else {
      Log.notice('[PLAYER]', eventName);
      this._fireAdapterEvent('[PLAYER] ' + eventName, {
        eventType: 'ContentPlayback'
      }, dimensions, metrics);
      if (this._userState !== null) {
        this._userState.setActive(eventName, playerStarted);
      }
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // CONTENT SEARCH
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Tracks search query events.
    * @param {string} searchQuery The search term entered by the user.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackSearchQuery: function (searchQuery, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track search query since Product Analytics is uninitialized.');
    } else if (typeof searchQuery !== 'string' || searchQuery === '') {
      Log.warn('Cannot track search query since no searchQuery has been supplied.');
    } else {
      Log.notice('[SEARCH] Query', searchQuery);
      this._searchQuery = searchQuery;
      this._fireEvent('[SEARCH] Query', {
        eventType: 'ContentSearch',
        query: this._searchQuery
      }, dimensions, metrics);
    }
  },
  /**
    * Tracks search result events.
    * @param {integer} resultCount The number of search results returned by a search query.
    * @param {String} [searchQuery] The search term entered by the user.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */
  trackSearchResult: function (resultCount, searchQuery, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track search result since Product Analytics is uninitialized.');
    } else if (typeof resultCount !== 'number') {
      Log.warn('Cannot track search result since no resultCount has been supplied.');
    } else {
      var query = typeof searchQuery === 'string' && searchQuery !== '' ? searchQuery : this._searchQuery;
      Log.notice('[SEARCH] Results', resultCount);
      this._fireEvent('[SEARCH] Results', {
        eventType: 'ContentSearch',
        query: query,
        resultCount: resultCount
      }, dimensions, metrics);
    }
  },
  /**
    * Tracks user interactions with search results.
    * @param {integer} column The content placement column. It is commonly used to indicate the column number where content is placed in a grid layout (i.e.1, 2, etc..).
    * @param {integer} row The content placement row. It is commonly used to indicate the row number where content is placed in a grid layout (i.e.1, 2, etc..).
    * @param {string} contentID The content identifier. It is used for internal content unequivocally identification (i.e., AAA000111222).
    * @param {String} [searchQuery] The search term entered by the user.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackSearchClick: function (column, row, contentID, searchQuery, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track search click since Product Analytics is uninitialized.');
    } else if (typeof column !== 'number') {
      Log.warn('Cannot track search click since no column has been supplied.');
    } else if (typeof row !== 'number') {
      Log.warn('Cannot track search click since no row has been supplied.');
    } else if (typeof contentID !== 'string' || contentID === '') {
      Log.warn('Cannot track search click since no contentID has been supplied.');
    } else {
      var query = typeof searchQuery === 'string' && searchQuery !== '' ? searchQuery : this._searchQuery;
      Log.notice('[SEARCH] Result Click');
      this._fireEvent('[SEARCH] Result Click', {
        eventType: 'ContentSearch',
        query: query,
        column: column,
        row: row,
        contentId: contentID
      }, dimensions, metrics);
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // EXTERNAL APPLICATIONS
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Tracks external app start events.
    * @param {string} appName The name of the application being used to deliver the content to the end-user (i.e., Netflix).
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackExternalAppLaunch: function (appName, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track external application launch since Product Analytics is uninitialized.');
    } else if (typeof appName !== 'string' || appName === '') {
      Log.warn('Cannot track external application launch since no appName has been supplied.');
    } else {
      Log.notice('APP LAUNCH', appName);
      this._fireEvent('APP LAUNCH', {
        eventType: 'ExternalApplications',
        appName: appName
      }, dimensions, metrics);
    }
  },
  /**
    * Tracks external app stop events.
    * @param {string} appName The name of the application being used to deliver the content to the end-user (i.e., Netflix).
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackExternalAppExit: function (appName, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track external application exit since Product Analytics is uninitialized.');
    } else if (typeof appName !== 'string' || appName === '') {
      Log.warn('Cannot track external application exit since no appName has been supplied.');
    } else {
      Log.notice('APP EXIT', appName);
      this._fireEvent('APP EXIT', {
        eventType: 'ExternalApplications',
        appName: appName
      }, dimensions, metrics);
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // ENGAGEMENT
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Tracks engagement events.
    * @param {string} eventName The name of the engagement event (i.e., Share, Save, Rate, etc.).
    * @param {string} contentID The unique content identifier of the content the user is engaging with.
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackEngagementEvent: function (eventName, contentID, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track engagement event since Product Analytics is uninitialized.');
    } else if (typeof eventName !== 'string' || eventName === '') {
      Log.warn('Cannot track engagement event since no eventName has been supplied.');
    } else if (typeof contentID !== 'string' || contentID === '') {
      Log.warn('Cannot track engagement event since no contentID has been supplied.');
    } else {
      Log.notice(eventName);
      this._fireEvent(eventName, {
        eventType: 'Engagement',
        contentId: contentID
      }, dimensions, metrics);
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // CUSTOM EVENT
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Track custom event
    * @param {string} eventName Name of the event to track
    * @param {Object} [dimensions] Dimensions to track
    * @param {Object} [metrics] Metrics to track
    */

  trackEvent: function (eventName, dimensions, metrics) {
    if (!this._initialized) {
      Log.warn('Cannot track event since Product Analytics is uninitialized.');
    } else if (typeof eventName !== 'string' || eventName === '') {
      Log.warn('Event cannot be tracked since no eventName has been supplied.');
    } else {
      Log.notice(eventName);
      this._fireEvent(eventName, {
        eventType: 'CustomEvent'
      }, dimensions, metrics);
    }
  },
  // ------------------------------------------------------------------------------------------------------------------------------
  // INTERNAL
  // ------------------------------------------------------------------------------------------------------------------------------

  /**
    * Fires an event
    * @param {string} eventName Name of the event to be fired
    * @param {Object} dimensionsInternal Dimensions supplied by user
    * @param {Object} dimensionsUser Specific event dimensions
    * @param {Object} metrics Metrics to track
    * @private
    */

  _fireEvent: function (eventName, dimensionsInternal, dimensionsUser, metrics) {
    var dimensions;
    var fired = false;

    // Extract top level dimensions from custom dimensions

    dimensions = this._buildDimensions(dimensionsInternal, dimensionsUser);

    // Track event

    if (this._infinity) {
      this._infinity.fireEvent(eventName, dimensions.custom, metrics, dimensions.top);
      fired = true;
    } else {
      Log.warn('Cannot fire', eventName, 'since infinity is unavailable.');
    }
    return fired;
  },
  /**
    * Fires an adapter event (in case it is available)
    * @param {string} eventName Event name
    * @param {Object} dimensionsInternal Dimensions supplied by user
    * @param {Object} dimensionsUser Specific event dimensions
    * @param {Object} metrics Metrics to track
    * @private
    */

  _fireAdapterEvent: function (eventName, dimensionsInternal, dimensionsUser, metrics) {
    var dimensions;
    var adapter;
    var fired = false;
    adapter = this._adapterGet();
    if (adapter) {
      dimensions = this._buildDimensions(dimensionsInternal, dimensionsUser);
      adapter.fireEvent(eventName, dimensions.custom, metrics, dimensions.top);
      fired = true;
    } else {
      Log.warn('Cannot fire ' + eventName + ' event since adapter is unavailable.');
    }
    return fired;
  },
  /**
    * Builds a list of top level and custom dimensions
    * @param {Object} dimensionsInternal Object containing list of internal dimensions
    * @param {Object} dimensionsUser Object containing list of custom dimensions
    * @private
    */

  _buildDimensions: function (dimensionsInternal, dimensionsUser) {
    var dimensionsTopLevel;
    var dimensionsCustom;
    var topKeysDelete;
    var topKeys;

    // Build custom event dimensions

    dimensionsCustom = {
      page: this._page
    };
    if (typeof dimensionsInternal !== 'undefined') {
      dimensionsCustom = Object.assign(dimensionsCustom, dimensionsInternal);
    }
    if (typeof dimensionsUser !== 'undefined') {
      dimensionsCustom = Object.assign(dimensionsCustom, dimensionsUser);
    }
    dimensionsCustom = Object.assign(dimensionsCustom, {
      eventSource: 'Product Analytics'
    });

    // List of Top Level Dimension keys

    topKeys = ['contentid', 'contentId', 'contentID', 'utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'];
    topKeysDelete = ['contentid', 'contentId', 'contentID'];

    // Create object with top level dimensions

    dimensionsTopLevel = {};
    for (var key in dimensionsCustom) {
      if (Object.prototype.hasOwnProperty.call(dimensionsCustom, key) && topKeys.indexOf(key) !== -1) {
        dimensionsTopLevel[key] = dimensionsCustom[key];
      }
    }

    // Remove top level dimensions from custom dimensions list

    topKeysDelete.forEach(function (key) {
      delete dimensionsCustom[key];
    });
    return {
      custom: dimensionsCustom,
      top: dimensionsTopLevel
    };
  }
});
module.exports = YouboraProductAnalytics;